import React, { useMemo } from "react";
import { Pie } from "react-chartjs-2";
import { useSortBy, useTable } from "react-table";

const red = "#d71433";
const yellow = "#F8ED00";
const green = "#94ba33";
const orange = "#f57c00";

const lateColorFor = (assigned, late) => {
  const latePercent = assigned + late === 0 ? 0 : late / (assigned + late) * 100;
  if (latePercent < 12) {
    return yellow;
  } else if (latePercent < 20) {
    return orange;
  }
  return red;
};

const inspectorPieOptions = {
  maintainAspectRatio: false,
  legend: { display: false },
  tooltips: { enabled: false },
};

const statePieOptions = {
  maintainAspectRatio: false,
  legend: { display: false },
  tooltips: { enabled: false },
};

export const InspectorTable = ({ data }) => {
  const columns = useMemo(() => [
    { Header: "Inspector", accessor: "Inspector" },
    { Header: "Assigned", accessor: "Assigned" },
    {
      Header: "Late",
      accessor: "Late",
      Cell: props => `${props.row.original.Late} (${props.row.original.LatePercent.toFixed(0)}%)`,
    },
    {
      Header: "",
      accessor: "chart",
      Cell: props => {
        const assigned = Number(props.row.original.Assigned - props.row.original.Late);
        const late = Number(props.row.original.Late) || 0;
        return (
          <div className="tw-w-20 tw-h-14">
            <Pie
              data={{
                datasets: [{
                  data: [assigned, late],
                  backgroundColor: [green, lateColorFor(assigned, late)],
                }],
                labels: ["OK", "Late"],
              }}
              options={inspectorPieOptions}
            />
          </div>
        );
      },
    },
  ], []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({ columns, data }, useSortBy);

  return (
    <table className="tw-min-w-full tw-divide-y tw-divide-gray-200" {...getTableProps()}>
      <thead>
        {headerGroups.map((headerGroup, i) => 
          <tr key={i} className="tw-bg-fs-dark-blue tw-bg-opacity-25 tw-rounded-t-lg" {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column, j) => 
              <th
                key={`${i}-${j}`}
                className={`tw-px-6 tw-py-3 tw-text-left tw-leading-4 tw-font-medium tw-uppercase tw-tracking-wider tw-font-bold ${
                  column.isSorted ? column.isSortedDesc ? "sort-desc" : "sort-asc" : ""
                }`}
                {...column.getHeaderProps(column.getSortByToggleProps())}
              >
                {column.render("Header")}
                {column.isSorted && 
                  <div className="tw-absolute tw-inline tw--mt-1 tw-ml-1">
                    <i className={`fa fa-sort-${column.isSortedDesc ? "down" : "up"}`} />
                  </div>
                }
              </th>
            )}
          </tr>
        )}
      </thead>
      <tbody {...getTableBodyProps()}>
        {rows.map((row, i) => {
          prepareRow(row);
          return (
            <tr key={i} {...row.getRowProps()}>
              {row.cells.map((cell, j) => {
                const classnames = `tw-px-6 tw-py-2 ${i % 2 ? "tw-bg-gray-200" : ""}`;
                return (
                  <td key={`${i}-${j}`} className={classnames} {...cell.getCellProps()}>
                    {cell.render("Cell")}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export const StateTable = ({ data }) => {
  const getLatePercent = (row) => {
    if (row.Assigned === 0) {return "--";}
    return `${(row.Late / row.Assigned * 100).toFixed(0)}%`;
  };

  const columns = useMemo(() => [
    { Header: "State", accessor: "State" },
    { Header: "Assigned", accessor: "Assigned" },
    {
      Header: "Late",
      accessor: "Late",
      Cell: props => `${props.row.original.Late} (${getLatePercent(props.row.original)})`,
    },
    {
      Header: "",
      accessor: "chart",
      Cell: props => {
        const assigned = Number(props.row.original.Assigned - props.row.original.Late);
        const late = Number(props.row.original.Late) || 0;
        return (
          <div className="tw-w-20 tw-h-14">
            <Pie
              data={{
                datasets: [{
                  data: [assigned, late],
                  backgroundColor: [green, lateColorFor(assigned, late)],
                }],
                labels: ["OK", "Late"],
              }}
              options={statePieOptions}
            />
          </div>
        );
      },
    },
  ], []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({ columns, data, initialState: { sortBy: [{ id: "State", desc: false }] } }, useSortBy);

  return (
    <table className="tw-min-w-full tw-divide-y tw-divide-gray-200" {...getTableProps()}>
      <thead>
        {headerGroups.map((headerGroup, i) => 
          <tr key={i} className="tw-bg-fs-dark-blue tw-bg-opacity-25 tw-rounded-t-lg" {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column, j) => 
              <th
                key={`${i}-${j}`}
                className={`tw-px-6 tw-py-3 tw-text-left tw-leading-4 tw-font-medium tw-uppercase tw-tracking-wider tw-font-bold ${
                  column.isSorted ? column.isSortedDesc ? "sort-desc" : "sort-asc" : ""
                }`}
                {...column.getHeaderProps(column.getSortByToggleProps())}
              >
                {column.render("Header")}
                {column.isSorted && 
                  <div className="tw-absolute tw-inline tw--mt-1 tw-ml-1">
                    <i className={`fa fa-sort-${column.isSortedDesc ? "down" : "up"}`} />
                  </div>
                }
              </th>
            )}
          </tr>
        )}
      </thead>
      <tbody {...getTableBodyProps()}>
        {rows.map((row, i) => {
          prepareRow(row);
          return (
            <tr key={i} {...row.getRowProps()}>
              {row.cells.map((cell, j) => {
                const classnames = `tw-px-6 tw-py-2 ${i % 2 ? "tw-bg-gray-200" : ""}`;
                return (
                  <td key={`${i}-${j}`} className={classnames} {...cell.getCellProps()}>
                    {cell.render("Cell")}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};
